import React, { useEffect, useMemo, useState } from "react";
import { RotateCcw, Smartphone, Trash2, UserMinus, Search, Ban, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";

/** Single source of truth for managing active users + the devices they've
 * signed in from. Supports: disable/enable (soft delete), unbind one device,
 * unbind all devices, delete the user outright. Role + status badges let the
 * admin tell students / teachers and active / disabled apart at a glance. */
export default function DevicesTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q),
    );
  }, [users, query]);

  const resetAll = async (u) => {
    if (!window.confirm(`Unbind ALL devices for ${u.email}? They'll need to sign in again.`)) return;
    await api.post(`/admin/users/${u.user_id}/reset-device`);
    load();
  };
  const unbindOne = async (u, deviceId) => {
    if (!window.confirm(`Unbind this device for ${u.email}?`)) return;
    await api.delete(`/admin/users/${u.user_id}/devices/${deviceId}`);
    load();
  };
  const toggleDisabled = async (u) => {
    const isDisabled = u.status === "disabled";
    const msg = isDisabled
      ? `Re-enable ${u.email}? They'll be able to sign in again.`
      : `Disable ${u.email}? They'll be signed out immediately and blocked from logging in until re-enabled. Their data stays intact.`;
    if (!window.confirm(msg)) return;
    await api.post(`/admin/users/${u.user_id}/${isDisabled ? "enable" : "disable"}`);
    load();
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email} permanently? This removes their account, sessions and uploads. Their email stays on the allowlist — remove it separately if needed.`)) return;
    await api.delete(`/admin/users/${u.user_id}`);
    load();
  };

  return (
    <div className="space-y-4" data-testid="devices-tab">
      {/* Search */}
      <div className="relative">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by email or name…"
          data-testid="devices-search"
          className="w-full ps-9 pe-3 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage"
        />
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-border rounded-2xl overflow-hidden" data-testid="users-list">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-start px-4 py-3">User</th>
              <th className="text-start px-4 py-3">Role</th>
              <th className="text-start px-4 py-3">Devices</th>
              <th className="text-start px-4 py-3">Joined</th>
              <th className="w-44"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                {query ? "No match" : "No users have signed in yet"}
              </td></tr>
            )}
            {!loading && filtered.map((u) => (
              <UserRow
                key={u.user_id}
                user={u}
                onUnbindAll={() => resetAll(u)}
                onUnbindOne={(dev) => unbindOne(u, dev)}
                onToggleDisabled={() => toggleDisabled(u)}
                onDelete={() => deleteUser(u)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2.5" data-testid="users-list-mobile">
        {!loading && filtered.length === 0 && (
          <div className="text-center text-sm text-muted-foreground py-6 bg-white border border-border rounded-2xl">
            {query ? "No match" : "No users have signed in yet"}
          </div>
        )}
        {!loading && filtered.map((u) => (
          <UserCard
            key={u.user_id}
            user={u}
            onUnbindAll={() => resetAll(u)}
            onUnbindOne={(dev) => unbindOne(u, dev)}
            onToggleDisabled={() => toggleDisabled(u)}
            onDelete={() => deleteUser(u)}
          />
        ))}
      </div>
    </div>
  );
}

function roleBadge(role) {
  const r = role || "student";
  const cls = r === "teacher"
    ? "bg-terracotta/10 text-terracotta"
    : "bg-sage/10 text-sage";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${cls}`}>
      {r}
    </span>
  );
}

function statusBadge(status) {
  if (status === "disabled") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-muted text-muted-foreground" title="Account disabled — signed out + login blocked">
        <Ban className="w-3 h-3" /> disabled
      </span>
    );
  }
  return null;
}

function normaliseDevices(u) {
  if (u.devices && u.devices.length) return u.devices;
  if (u.device_id) return [{ device_id: u.device_id, label: null, bound_at: u.created_at }];
  return [];
}

function UserRow({ user: u, onUnbindAll, onUnbindOne, onToggleDisabled, onDelete }) {
  const devs = normaliseDevices(u);
  const isDisabled = u.status === "disabled";
  return (
    <tr className={`border-t border-border align-top ${isDisabled ? "bg-secondary/40" : ""}`} data-testid={`user-row-${u.user_id}`}>
      <td className="px-4 py-3">
        <div className={`font-medium ${isDisabled ? "text-muted-foreground line-through" : ""}`}>{u.name || "—"}</div>
        <div className="text-xs text-muted-foreground">{u.email}</div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1 items-start">
          {roleBadge(u.role)}
          {statusBadge(u.status)}
        </div>
      </td>
      <td className="px-4 py-3">
        {devs.length === 0 ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <div className="space-y-1.5">
            {devs.map((d) => (
              <div key={d.device_id} className="flex items-center gap-2 text-xs">
                <Smartphone className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="font-mono text-[10px] text-muted-foreground">{(d.device_id || "").slice(0, 14)}…</span>
                <button
                  onClick={() => onUnbindOne(d.device_id)}
                  data-testid={`unbind-${u.user_id}-${(d.device_id || "").slice(0, 8)}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-terracotta hover:bg-terracotta/10"
                >
                  <Trash2 className="w-3 h-3" /> unbind
                </button>
              </div>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
      <td className="px-4 py-3 text-end">
        <div className="inline-flex flex-col gap-1.5">
          <button
            onClick={onToggleDisabled}
            data-testid={`toggle-disabled-${u.user_id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
              isDisabled
                ? "bg-sage hover:bg-sage-dark text-white"
                : "bg-white border border-amber-300 text-amber-800 hover:bg-amber-50"
            }`}
          >
            {isDisabled ? <><CheckCircle2 className="w-3.5 h-3.5" /> Enable</> : <><Ban className="w-3.5 h-3.5" /> Disable</>}
          </button>
          <button
            onClick={onUnbindAll}
            disabled={devs.length === 0}
            data-testid={`reset-device-${u.user_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-border hover:bg-secondary disabled:opacity-40 text-xs font-medium"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Unbind all
          </button>
          <button
            onClick={onDelete}
            data-testid={`delete-user-${u.user_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-terracotta/40 text-terracotta hover:bg-terracotta/10 text-xs font-medium"
          >
            <UserMinus className="w-3.5 h-3.5" /> Delete user
          </button>
        </div>
      </td>
    </tr>
  );
}

function UserCard({ user: u, onUnbindAll, onUnbindOne, onToggleDisabled, onDelete }) {
  const devs = normaliseDevices(u);
  const isDisabled = u.status === "disabled";
  return (
    <div className={`bg-white border border-border rounded-2xl p-4 ${isDisabled ? "bg-secondary/40" : ""}`} data-testid={`user-card-${u.user_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={`font-medium truncate ${isDisabled ? "text-muted-foreground line-through" : ""}`}>{u.name || "—"}</div>
          <div className="text-xs text-muted-foreground truncate">{u.email}</div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {roleBadge(u.role)}
          {statusBadge(u.status)}
        </div>
      </div>
      {devs.length === 0 ? (
        <div className="mt-3 text-xs text-muted-foreground">No devices bound</div>
      ) : (
        <div className="mt-3 space-y-1.5">
          {devs.map((d) => (
            <div key={d.device_id} className="flex items-center justify-between gap-2 text-xs bg-bone rounded-lg px-2 py-1.5">
              <div className="flex items-center gap-1.5 min-w-0">
                <Smartphone className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="font-mono text-[10px] text-muted-foreground truncate">{(d.device_id || "").slice(0, 18)}…</span>
              </div>
              <button
                onClick={() => onUnbindOne(d.device_id)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] text-terracotta hover:bg-terracotta/10 shrink-0"
              >
                <Trash2 className="w-3 h-3" /> unbind
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-border grid grid-cols-3 gap-2">
        <button
          onClick={onToggleDisabled}
          className={`inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium ${
            isDisabled
              ? "bg-sage hover:bg-sage-dark text-white"
              : "bg-white border border-amber-300 text-amber-800 hover:bg-amber-50"
          }`}
        >
          {isDisabled ? <><CheckCircle2 className="w-3.5 h-3.5" /> Enable</> : <><Ban className="w-3.5 h-3.5" /> Disable</>}
        </button>
        <button
          onClick={onUnbindAll}
          disabled={devs.length === 0}
          className="inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-white border border-border hover:bg-secondary disabled:opacity-40 text-xs font-medium"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Unbind
        </button>
        <button
          onClick={onDelete}
          className="inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-white border border-terracotta/40 text-terracotta hover:bg-terracotta/10 text-xs font-medium"
        >
          <UserMinus className="w-3.5 h-3.5" /> Delete
        </button>
      </div>
    </div>
  );
}
